import { useState, useEffect } from 'react';
import { axiosInstance } from '@/axiosConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Plus, Pencil, Trash2, Check, ChevronsUpDown, 
  Building2, Map, Car, BedDouble, Plane, Ship, Briefcase, User, Eye
} from 'lucide-react'; 
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem,
} from '@/components/ui/command';

import { BANCOS_BRASIL } from '@/lib/bancos.js'; 

const TIPOS_FORNECEDOR = [
  "Agência de viagem", "Milhas", "Companhia aérea", "Hotel / Pousada",
  "Operadora Turística", "Receptivo / Transfer", "Seguro Viagem",
  "Locadora de Veículos", "Cruzeiro", "Consolidadora", "Guia Turístico", "Outros"
];

const TIPOS_CHAVE_PIX = ["CPF", "CNPJ", "Email", "Telefone", "Chave Aleatória"];

const initialFornecedorForm = {
  nome: '', contato: '', tipo: '', email: '', telefone: '',
  cnpj: '', pix: '', pix_tipo: '', pix_banco: '', observacao: ''
};

export default function Fornecedores() {
  const [fornecedores, setFornecedores] = useState([]);
  const [loading, setLoading] = useState(true);

  // Estados
  const [openFornecedor, setOpenFornecedor] = useState(false);
  const [editingFornecedorId, setEditingFornecedorId] = useState(null);
  const [fornecedorForm, setFornecedorForm] = useState(initialFornecedorForm); 
  const [openFornecedorDetails, setOpenFornecedorDetails] = useState(false);
  const [selectedFornecedor, setSelectedFornecedor] = useState(null);
  const [openFornecedorBancoPopover, setOpenFornecedorBancoPopover] = useState(false);

  useEffect(() => {
    loadFornecedores();
  }, []);

  const loadFornecedores = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/fornecedores');
      setFornecedores(res.data);
    } catch {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleFornecedorSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...fornecedorForm };
      payload.pix = payload.pix || '';
      payload.pix_tipo = payload.pix_tipo || '';
      payload.pix_banco = payload.pix_banco || '';

      if (editingFornecedorId) {
        await axiosInstance.put(`/fornecedores/${editingFornecedorId}`, payload);
        toast.success('Fornecedor atualizado!');
      } else {
        await axiosInstance.post('/fornecedores', payload);
        toast.success('Fornecedor cadastrado!');
      }
      setOpenFornecedor(false);
      setEditingFornecedorId(null);
      setFornecedorForm(initialFornecedorForm);
      loadFornecedores();
    } catch {
      toast.error('Erro ao salvar fornecedor');
    }
  };

  const handleEditFornecedor = (f) => {
    setEditingFornecedorId(f.id);
    setFornecedorForm({
        nome: f.nome || '', contato: f.contato || '', tipo: f.tipo || '',
        email: f.email || '', telefone: f.telefone || '', cnpj: f.cnpj || '',
        pix: f.pix || '', pix_tipo: f.pix_tipo || '', pix_banco: f.pix_banco || '',
        observacao: f.observacao || ''
    });
    setOpenFornecedor(true);
  };

  const handleDeleteFornecedor = async (id) => {
    try {
      await axiosInstance.delete(`/fornecedores/${id}`);
      toast.success('Fornecedor deletado!');
      loadFornecedores();
    } catch {
      toast.error('Erro ao deletar fornecedor');
    }
  };

  const getFornecedorIcon = (tipo) => {
    switch (tipo) {
      case "Companhia aérea": return <Plane className="w-4 h-4 text-blue-500" />;
      case "Milhas": return <Plane className="w-4 h-4 text-blue-500" />;
      case "Hotel / Pousada": return <BedDouble className="w-4 h-4 text-orange-500" />;
      case "Locadora de Veículos": 
      case "Receptivo / Transfer": return <Car className="w-4 h-4 text-green-500" />;
      case "Cruzeiro": return <Ship className="w-4 h-4 text-cyan-500" />;
      case "Seguro Viagem": return <Shield className="w-4 h-4 text-red-500" />;
      case "Operadora Turística": 
      case "Guia Turístico": return <Map className="w-4 h-4 text-purple-500" />;
      case "Agência de viagem":
      case "Consolidadora": return <Briefcase className="w-4 h-4 text-indigo-500" />;
      default: return <Building2 className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="flex justify-between items-center mb-6 pt-8">
         <div>
            <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Fornecedores</h1>
            <p className="text-gray-600">Parceiros e serviços contratados</p>
         </div>
         <Button onClick={() => { setEditingFornecedorId(null); setFornecedorForm(initialFornecedorForm); setOpenFornecedor(true); }} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <Plus className="w-4 h-4 mr-2" /> Novo Fornecedor
         </Button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contato</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {fornecedores.map((f) => (
              <tr key={f.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600 mr-3"><Building2 className="w-5 h-5" /></div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{f.nome}</div>
                      {f.cnpj && <div className="text-xs text-gray-400">{f.cnpj}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                      {getFornecedorIcon(f.tipo)} 
                      <span className="text-sm text-gray-700 font-medium">{f.tipo || 'Outros'}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col">
                      <div className="flex items-center text-sm text-gray-900 mb-0.5"><User className="w-3 h-3 mr-1.5 text-gray-400" />{f.contato || 'S/ Contato'}</div>
                      {(f.telefone || f.email) && <div className="text-xs text-gray-500 pl-5">{f.telefone || f.email}</div>}
                  </div>
                </td>
                <td className="px-6 py-4 text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => {setSelectedFornecedor(f); setOpenFornecedorDetails(true);}} className="mr-1 hover:bg-blue-50 hover:text-blue-600"><Eye className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleEditFornecedor(f)} className="mr-1 hover:bg-yellow-50 hover:text-yellow-600"><Pencil className="w-4 h-4" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button variant="ghost" size="sm" className="hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle><AlertDialogDescription>Deseja deletar este fornecedor?</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteFornecedor(f.id)} style={{ background: '#ef4444' }}>Deletar</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MODAL FORMULÁRIO */}
      <Dialog open={openFornecedor} onOpenChange={setOpenFornecedor}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingFornecedorId ? 'Editar' : 'Novo'} Fornecedor</DialogTitle></DialogHeader>
          <form onSubmit={handleFornecedorSubmit} className="space-y-4 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Nome da Empresa / Razão Social</Label>
                    <Input value={fornecedorForm.nome} onChange={(e) => setFornecedorForm({ ...fornecedorForm, nome: e.target.value })} required />
                  </div>
                  <div>
                    <Label>CNPJ / CPF</Label>
                    <Input value={fornecedorForm.cnpj} onChange={(e) => setFornecedorForm({ ...fornecedorForm, cnpj: e.target.value })} placeholder="00.000.000/0000-00" />
                  </div>
                  <div>
                    <Label>Tipo de Serviço</Label>
                    <Select value={fornecedorForm.tipo} onValueChange={(val) => setFornecedorForm({ ...fornecedorForm, tipo: val })} required>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Selecione o tipo" /></SelectTrigger>
                      <SelectContent>{TIPOS_FORNECEDOR.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Pessoa de Contato</Label><Input value={fornecedorForm.contato} onChange={(e) => setFornecedorForm({ ...fornecedorForm, contato: e.target.value })} /></div>
                  <div><Label>Telefone / WhatsApp</Label><Input value={fornecedorForm.telefone} onChange={(e) => setFornecedorForm({ ...fornecedorForm, telefone: e.target.value })} /></div>
                  <div className="md:col-span-2"><Label>Email Comercial</Label><Input type="email" value={fornecedorForm.email} onChange={(e) => setFornecedorForm({ ...fornecedorForm, email: e.target.value })} /></div>
                  
                  {/* ÁREA PIX */}
                  <div className="md:col-span-2 border rounded-lg p-4 bg-gray-50 space-y-3">
                      <Label className="text-blue-600 font-semibold">Dados Bancários (Pix)</Label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                              <Label className="text-xs text-gray-500">Tipo de Chave</Label>
                              <Select value={fornecedorForm.pix_tipo} onValueChange={(val) => setFornecedorForm({ ...fornecedorForm, pix_tipo: val })}>
                                  <SelectTrigger className="bg-white"><SelectValue placeholder="Tipo" /></SelectTrigger>
                                  <SelectContent>{TIPOS_CHAVE_PIX.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                              </Select>
                          </div>
                          <div>
                              <Label className="text-xs text-gray-500">Chave Pix</Label>
                              <Input value={fornecedorForm.pix} onChange={(e) => setFornecedorForm({ ...fornecedorForm, pix: e.target.value })} className="bg-white" />
                          </div>
                          <div>
                              <Label className="text-xs text-gray-500">Banco</Label>
                              <Popover open={openFornecedorBancoPopover} onOpenChange={setOpenFornecedorBancoPopover}>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" role="combobox" className="w-full justify-between bg-white">
                                    {fornecedorForm.pix_banco ? fornecedorForm.pix_banco : "Selecione..."}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                                  <Command>
                                    <CommandInput placeholder="Pesquisar..." />
                                    <CommandEmpty>Não encontrado.</CommandEmpty>
                                    <CommandGroup className="max-h-60 overflow-y-auto">
                                      {BANCOS_BRASIL.map((banco) => (
                                        <CommandItem key={banco} value={banco} onSelect={(val) => {
                                            setFornecedorForm({ ...fornecedorForm, pix_banco: val });
                                            setOpenFornecedorBancoPopover(false);
                                          }}>
                                          <Check className={`mr-2 h-4 w-4 ${fornecedorForm.pix_banco === banco ? "opacity-100" : "opacity-0"}`} />
                                          {banco}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                          </div>
                      </div>
                  </div>
                  <div className="md:col-span-2"><Label>Observações</Label><Input value={fornecedorForm.observacao} onChange={(e) => setFornecedorForm({ ...fornecedorForm, observacao: e.target.value })} placeholder="Detalhes de contrato, prazos, etc." /></div>
              </div>
              <Button type="submit" className="w-full mt-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                {editingFornecedorId ? 'Atualizar Fornecedor' : 'Cadastrar Fornecedor'}
              </Button>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* MODAL DETALHES (Mantido idêntico) */}
      <Dialog open={openFornecedorDetails} onOpenChange={setOpenFornecedorDetails}>
          <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle>Detalhes do Fornecedor</DialogTitle></DialogHeader>
              {selectedFornecedor && (
              <div className="space-y-4 py-2">
                  <div>
                      <h3 className="font-semibold text-lg">{selectedFornecedor.nome}</h3>
                      <span className="inline-block px-2 py-1 text-xs rounded bg-blue-50 text-blue-700 border border-blue-100">{selectedFornecedor.tipo}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm border-t border-gray-100 pt-4">
                     <div><Label className="text-xs text-gray-500">CNPJ / CPF</Label><p className="font-medium">{selectedFornecedor.cnpj || '-'}</p></div>
                     <div><Label className="text-xs text-gray-500">Contato</Label><p className="font-medium">{selectedFornecedor.contato || '-'}</p></div>
                     <div><Label className="text-xs text-gray-500">Telefone</Label><p className="font-medium">{selectedFornecedor.telefone || '-'}</p></div>
                     <div><Label className="text-xs text-gray-500">Email</Label><p className="font-medium truncate" title={selectedFornecedor.email}>{selectedFornecedor.email || '-'}</p></div>
                     <div className="col-span-2 pt-2 border-t border-gray-100">
                        <Label className="text-xs text-gray-500">Dados Bancários</Label>
                        <div className="bg-gray-50 p-3 rounded border border-gray-100 mt-1">
                            <div className="grid grid-cols-2 gap-2">
                                <div><span className="text-xs text-gray-400 block">Banco</span><span className="text-sm font-medium text-gray-700">{selectedFornecedor.pix_banco || '-'}</span></div>
                                <div><span className="text-xs text-gray-400 block">Tipo</span><span className="text-sm font-medium text-gray-700">{selectedFornecedor.pix_tipo || '-'}</span></div>
                                <div className="col-span-2"><span className="text-xs text-gray-400 block">Chave Pix</span><span className="font-mono text-sm font-semibold select-all">{selectedFornecedor.pix || '-'}</span></div>
                            </div>
                        </div>
                     </div>
                     {selectedFornecedor.observacao && (
                        <div className="col-span-2"><Label className="text-xs text-gray-500">Observações</Label><p className="text-sm text-gray-700 bg-yellow-50 p-2 rounded border border-yellow-100 mt-1">{selectedFornecedor.observacao}</p></div>
                     )}
                  </div>
              </div>
              )}
              <DialogFooter><Button onClick={() => setOpenFornecedorDetails(false)}>Fechar</Button></DialogFooter>
          </DialogContent>
      </Dialog>
    </div>
  );
}